import { ModernSelect, SelectOption } from "@components/Select";
import {
  BUTTON_PRIMARY_ICON_FONT_SIZE,
  BUTTON_SECONDARY_ICON_FONT_SIZE
} from "@constants/button";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Divider,
  Fab,
  Grid,
  IconButton,
  SelectChangeEvent,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useClientModelAggregationType } from "@services/api";
import { getTextFieldSx } from "@styles/TextField";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsFiletypeCsv, BsFiletypeJson } from "react-icons/bs";

export interface ClientModelCreateForm {
  name: string;
  year: number;
  aggregation_type: string;
  input_file: File | null;
  output_file: File | null;
  parameters_file: File | null;
  baseline_organic_file: File | null;
}

export interface ClientModelFormProps {
  initialModel: ClientModelCreateForm;
  onSave: (event: any, model: ClientModelCreateForm) => void;
  onGoBack: () => void;
  processing: boolean;
}

const ClientModelForm = ({
  initialModel,
  onSave,
  onGoBack,
  processing
}: ClientModelFormProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [modelYear, setModelYear] = useState<number>(
    initialModel.year > 0 ? initialModel.year : currentYear
  );
  const [modelName, setModelName] = useState<string>("");
  const [aggregationType, setAggregationType] = useState<string>("");
  const [inputFile, setInputFile] = useState<File | null>(
    initialModel.input_file
  );
  const [outputFile, setOutputFile] = useState<File | null>(
    initialModel.output_file
  );
  const [parametersFile, setParametersFile] = useState<File | null>(
    initialModel.parameters_file
  );
  const [baselineOrganicFile, setBaselineOrganicFile] = useState<File | null>(
    initialModel.output_file
  );

  const { data: aggregationTypes, isFetching: isLoading } =
    useClientModelAggregationType();

  const handleUploadClick = (event: any) => {
    setInputFile(event.target.files[0]);
  };
  const handleUploadOutputClick = (event: any) => {
    setOutputFile(event.target.files[0]);
  };
  const handleUploadParametersClick = (event: any) => {
    setParametersFile(event.target.files[0]);
  };
  const handleUploadBaselineOrganicClick = (event: any) => {
    setBaselineOrganicFile(event.target.files[0]);
  };

  const textFieldSx = useMemo(() => getTextFieldSx(theme), [theme]);

  const onAggregationTypeSelectChange = (event: SelectChangeEvent<string>) => {
    const aggregationTypeId = event.target.value;
    setAggregationType(aggregationTypeId.toString());
  };

  const aggregationTypeOptions = useMemo(() => {
    return (aggregationTypes || [])
      .sort((a, b) => a.value.localeCompare(b.value))
      .reduce((acc, aggType) => {
        acc.push({
          value: aggType.value,
          label: t(`clientModel.aggregation_types.${aggType.value}`)
        });
        return acc;
      }, [] as SelectOption[]);
  }, [aggregationTypes]);

  return (
    <Box
      component="form"
      sx={{
        "& .MuiTextField-root": { p: 1 },
        width: "100%",
        height: "100%"
      }}
      noValidate
      autoComplete="off"
      className="p-8"
      onSubmit={(event) =>
        onSave(event, {
          name: modelName,
          year: modelYear,
          aggregation_type: aggregationType,
          input_file: inputFile,
          output_file: outputFile,
          parameters_file: parametersFile,
          baseline_organic_file: baselineOrganicFile
        })
      }
    >
      <Grid container spacing={2} justifyContent="center">
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center" }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newClient.general_information")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }} sx={{ justifyContent: "center" }}>
          <Box className="w-full">
            <TextField
              id="new-model-name"
              key="new-model-name"
              label={t("clientModel.name")}
              color="info"
              value={modelName}
              onChange={(event) => setModelName(event.target.value)}
              required
              fullWidth
              sx={textFieldSx}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }} sx={{ justifyContent: "center" }}>
          <Box className="w-full">
            <TextField
              id="new-model-year"
              key="new-model-year"
              label={t("clientModel.year")}
              color="info"
              value={modelYear}
              onChange={(event) => setModelYear(parseInt(event.target.value))}
              required
              fullWidth
              type="number"
              inputProps={{ min: currentYear - 2, max: currentYear + 2 }}
              sx={textFieldSx}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }} sx={{ justifyContent: "center" }}>
          <Box className="flex justify-center w-full p-2 h-full">
            {isLoading ? (
              <Skeleton variant="rectangular" width="100%" height={40} />
            ) : (
              <ModernSelect
                options={aggregationTypeOptions}
                value={aggregationType}
                onChange={onAggregationTypeSelectChange}
                valueKey="id"
                labelKey="name"
                label={`${t("newClientModel.selectAggregationType")} *`}
                showIndicatorDot={true}
                colorTheme="light"
                id="mmm-model-currency-helper"
                disabled={isLoading}
                sx={{ height: "100%" }}
              />
            )}
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center", marginTop: 4 }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newClient.mmm_model_information")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <Box className="flex flex-col items-center">
            {inputFile && (
              <Box className="flex flex-row items-center">
                <Typography variant="body2" className="pb-2">
                  {inputFile.name}
                </Typography>
                <IconButton color="error" onClick={() => setInputFile(null)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            <input
              accept="text/csv"
              className="hidden"
              id="input-button-file"
              type="file"
              onChange={handleUploadClick}
            />
            <label htmlFor="input-button-file">
              <Box className="flex flex-col items-center mt-4">
                <Typography gutterBottom variant="body2" className="pb-2">
                  {t("newClientModel.input_file")}
                  {" *"}
                </Typography>
                <Fab component="span">
                  <BsFiletypeCsv
                    style={{
                      fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE,
                      color: theme.palette.info.main
                    }}
                  />
                </Fab>
              </Box>
            </label>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <Box className="flex flex-col items-center">
            {outputFile && (
              <Box className="flex flex-row items-center">
                <Typography variant="body2" className="pb-2">
                  {outputFile.name}
                </Typography>
                <IconButton color="error" onClick={() => setOutputFile(null)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            <input
              accept="text/csv"
              className="hidden"
              id="ouput-button-file"
              type="file"
              onChange={handleUploadOutputClick}
            />
            <label htmlFor="ouput-button-file">
              <Box className="flex flex-col items-center mt-4">
                <Typography gutterBottom variant="body2" className="pb-2">
                  {t("newClientModel.output_file")}
                  {" *"}
                </Typography>
                <Fab component="span">
                  <BsFiletypeCsv
                    style={{
                      fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE,
                      color: theme.palette.info.main
                    }}
                  />
                </Fab>
              </Box>
            </label>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <Box className="flex flex-col items-center">
            {parametersFile && (
              <Box className="flex flex-row items-center">
                <Typography variant="body2" className="pb-2">
                  {parametersFile.name}
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => setParametersFile(null)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            <input
              accept="application/json"
              className="hidden"
              id="params-button-file"
              type="file"
              onChange={handleUploadParametersClick}
            />
            <label htmlFor="params-button-file">
              <Box className="flex flex-col items-center mt-4">
                <Typography gutterBottom variant="body2" className="pb-2">
                  {t("newClientModel.parameters_file")}
                  {" *"}
                </Typography>
                <Fab component="span">
                  <BsFiletypeJson
                    style={{
                      fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE,
                      color: theme.palette.info.main
                    }}
                  />
                </Fab>
              </Box>
            </label>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <Box className="flex flex-col items-center">
            {baselineOrganicFile && (
              <Box className="flex flex-row items-center">
                <Typography variant="body2" className="pb-2">
                  {baselineOrganicFile.name}
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => setBaselineOrganicFile(null)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            <input
              accept="text/csv"
              className="hidden"
              id="baseline-organic-button-file"
              type="file"
              onChange={handleUploadBaselineOrganicClick}
            />
            <label htmlFor="baseline-organic-button-file">
              <Box className="flex flex-col items-center mt-4">
                <Typography gutterBottom variant="body2" className="pb-2">
                  {t("newClientModel.baseline_organic_file")}
                </Typography>
                <Fab component="span">
                  <BsFiletypeCsv
                    style={{
                      fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE,
                      color: theme.palette.info.main
                    }}
                  />
                </Fab>
              </Box>
            </label>
          </Box>
        </Grid>
      </Grid>
      <Box className="flex justify-end p-8 gap-4">
        <Tooltip title={t("common.discard")}>
          <IconButton color="error" onClick={onGoBack} disabled={processing}>
            <CancelIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("common.save")}>
          <IconButton color="success" type="submit" disabled={processing}>
            <SaveIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ClientModelForm;
